import { useCallback, useEffect, useRef, useState } from "react";
import ChatIcon from "@mui/icons-material/Chat";
import DeleteForeverIcon from "@mui/icons-material/DeleteForeverOutlined";
import {
  BookingAuditMessage,
  BookingAuditSource,
  BookingAuditTypes,
  BookingStatus,
  ClockTypes,
  Providers,
  Status,
} from "@repo/database";
import {
  DEFAULT_BOOKING_DURATION,
  DEFAULT_BOOKING_GROUP_SIZE,
  getDateFNSLocale,
  isSameOrAfter,
  isSameOrBefore,
  isSameTime,
  nearestThirtyMinutes,
} from "@repo/utils";
import { BookingSchema } from "@repo/validators";
import {
  addMinutes,
  differenceInMinutes,
  format,
  getMinutes,
  isSameDay,
  set,
} from "date-fns";
import { Book, ClockIcon, SendIcon } from "lucide-react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import type { PartialBooking } from "~/providers/booking-provider";
import type { Section } from "~/providers/section-provider";
import type { Shift } from "~/providers/shift-provider";
import { useBooking } from "~/hooks/use-booking";
import { useEmployee } from "~/hooks/use-employee";
import { useLabel } from "~/hooks/use-label";
import { useSection } from "~/hooks/use-section";
import { useShift } from "~/hooks/use-shift";
import { useTable } from "~/hooks/use-table";
import { useUser } from "~/hooks/use-user";
import { bookingWithinShift, extractDirtyFields } from "~/lib/utils";
import { useBookingManager } from "~/managers/use-booking-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { useTempBookingStore } from "~/stores/use-booking-store";
import { isBookingAuditMessage } from "~/types";
import LabelIcon from "../icons/label-icon";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Checkbox } from "../ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";
import { PhoneInput } from "../ui/phone-input";
import { TabPanel, Tabs } from "../ui/smooth-tabs";
import { Textarea } from "../ui/textarea";
import VrodexPopover from "../ui/vrodex-popover";
import { BookingStatusSelect } from "./booking-status-select";

const TimePicker = ({
  value,
  onChange,
}: {
  value: Date;
  onChange: (value: Date) => void;
}) => {
  const start = set(value, {
    hours: 7,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const end = set(value, {
    hours: 23,
    minutes: 45,
    seconds: 0,
    milliseconds: 0,
  });

  const timeBetween = differenceInMinutes(end, start);

  const amountOfTimeslots = timeBetween / 15;

  const selectedButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (selectedButtonRef.current) {
      selectedButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [value]);

  return (
    <div className="flex flex-row gap-10">
      {/* Left column: 00 and 15 minute slots */}
      <div className="grid grid-cols-2 gap-4">
        {Array.from({ length: amountOfTimeslots + 1 }).map((_, idx) => {
          const currentTime = addMinutes(start, idx * 15);
          const minutes = getMinutes(currentTime);

          // Only show if minutes are 00 or 15
          if (minutes !== 0 && minutes !== 15) return null;

          const isCurrentTime = isSameTime(value, currentTime);

          return (
            <Button
              ref={isCurrentTime ? selectedButtonRef : null}
              key={currentTime.getTime()}
              type="button"
              onClick={() => onChange(currentTime)}
              variant={isCurrentTime ? "default" : "outline"}
            >
              {format(currentTime, "HH:mm")}
            </Button>
          );
        })}
      </div>

      {/* Right column: 30 and 45 minute slots */}
      <div className="grid grid-cols-2 gap-4">
        {Array.from({ length: amountOfTimeslots + 1 }).map((_, idx) => {
          const currentTime = addMinutes(start, idx * 15);
          const minutes = getMinutes(currentTime);

          // Only show if minutes are 30 or 45
          if (minutes !== 30 && minutes !== 45) return null;

          const isCurrentTime = isSameTime(value, currentTime);

          return (
            <Button
              ref={isCurrentTime ? selectedButtonRef : null}
              key={currentTime.getTime()}
              type="button"
              onClick={() => onChange(currentTime)}
              variant={isCurrentTime ? "default" : "outline"}
            >
              {format(currentTime, "HH:mm")}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export function BookingForm({
  onClose,
  booking,
  isBlock = false,
}: {
  onClose: (force?: boolean) => void;
  booking?: PartialBooking;
  isBlock?: boolean;
}) {
  const { tables } = useTable();

  const { sections } = useSection();

  const { labels } = useLabel();

  const { user } = useUser();

  const { employees } = useEmployee();

  const { bookings, bookingConfiguration } = useBooking();

  const [matchedShift, setMatchedShift] = useState<Shift | undefined>(
    undefined,
  );

  const { setUnsavedChanges } = useUnsavedChanges();

  const {
    createBooking,
    deleteBooking,
    updateBooking,
    updateStatus,
    sendMessageToGuest,
  } = useBookingManager();

  const [messageToCustomer, setMessageToCustomer] = useState("");

  const [selectedSection, setSelectedSection] = useState<Section | undefined>(
    sections[0],
  );

  const { shifts } = useShift();

  const { selectedDate } = useTempBookingStore();

  const [dateOpen, setDateOpen] = useState(false);

  const [startTimeOpen, setStartTimeOpen] = useState(false);

  const [endTimeOpen, setEndTimeOpen] = useState(false);

  const form = useForm({
    reValidateMode: "onChange",
    schema: BookingSchema,
    defaultValues: {
      id: booking?.id ?? uuidv4(),
      createdAt: booking?.createdAt ?? new Date(),
      firstName: booking?.firstName ?? "",
      status: Status.active,
      lastName: booking?.lastName ?? "",
      startTime:
        booking?.startTime ?? nearestThirtyMinutes(selectedDate, "floor"),
      endTime:
        booking?.endTime ??
        addMinutes(
          nearestThirtyMinutes(selectedDate, "floor"),
          DEFAULT_BOOKING_DURATION,
        ),
      tableIds: booking ? booking.tableIds : [],
      bookingStatus: booking?.bookingStatus ?? BookingStatus.active,
      groupSize: isBlock
        ? 0
        : (booking?.groupSize ?? DEFAULT_BOOKING_GROUP_SIZE),
      phone: booking?.phone ?? "",
      email: booking?.email ?? "",
      comment: booking?.comment ?? "",
      memo: booking?.memo ?? "",
      provider: Providers.allindex,
      labelIds: [],
    },
  });

  const startTime = useWatch({ control: form.control, name: "startTime" });

  const endTime = useWatch({ control: form.control, name: "endTime" });

  const groupSize = useWatch({ control: form.control, name: "groupSize" });

  const { reset, setError, clearErrors, setValue } = form;

  const customerEmail = useWatch({ control: form.control, name: "email" });

  const { t, i18n } = useTranslation("common");

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const locale = i18n.language;

  // const isLoading = isUpdatingBooking || isDeletingBooking;

  const handleSubmit = form.handleSubmit(async (data) => {
    if (booking?.id) {
      const { dirtyFields } = form.formState;

      const dirtyData = extractDirtyFields(data, dirtyFields);
      const result = await updateBooking({
        id: booking.id,
        ...dirtyData,
      });

      if (result === true) {
        form.reset();
        onClose(true);
      }
    } else {
      const result = await createBooking(data);
      if (result === true) {
        form.reset();
        onClose(true);
      }
    }
  });

  const handleDeleteBooking = async () => {
    if (!booking?.id) return;
    const result = await deleteBooking(booking.id);
    if (result === true) {
      onClose();
    }
  };

  const handleUpdateStatus = (id: string, bookingStatus: BookingStatus) => {
    void updateStatus({
      id,
      bookingStatus,
    });

    form.reset();
    onClose(true);
  };

  const handleSendMessageToGuest = async () => {
    if (!messageToCustomer || !customerEmail || !booking?.id) return;

    const result = await sendMessageToGuest(
      booking.id,
      customerEmail,
      messageToCustomer,
    );
    if (result === true) {
      setMessageToCustomer("");
    }
  };

  const getFirstAvailableTable = useCallback(() => {
    const bookingsOnDay = bookings.filter((booking) =>
      isSameDay(booking.startTime, selectedDate),
    );

    // Filter and sort tables by closest seat count that is greater than or equal to groupSize
    const suitableTables = tables
      .filter((table) => table.maxSeats >= groupSize)
      .sort((a, b) => a.maxSeats - b.maxSeats);

    // Find first available table
    const firstAvailableTable = suitableTables.find((table) => {
      const bookingsOnTable = bookingsOnDay.filter(
        (booking) =>
          booking.tableIds.includes(table.id) &&
          isSameOrAfter(booking.endTime, startTime) &&
          isSameOrBefore(booking.startTime, startTime),
      );
      return bookingsOnTable.length === 0;
    });

    if (firstAvailableTable) {
      form.setValue("tableIds", [firstAvailableTable.id]);
    } else {
      // alert("No available tables");
    }
  }, [bookings, form, groupSize, selectedDate, startTime, tables]);

  const findMatchedShift = useCallback(
    (start: Date, updateEndTime = false) => {
      const matchedShifts = bookingWithinShift(shifts, start);

      if (matchedShifts.length === 0) {
        setError("activeShiftWarning", {
          message: t("Booking.active_shift_warning"),
        });
      } else {
        clearErrors("activeShiftWarning");
      }
      if (updateEndTime) {
        setValue(
          "endTime",
          addMinutes(
            start,
            matchedShifts[0]?.standardDuration ??
              bookingConfiguration?.bookingDuration ??
              120,
          ),
          { shouldDirty: true },
        );
      }
    },
    [
      shifts,
      setError,
      t,
      clearErrors,
      setValue,
      bookingConfiguration?.bookingDuration,
    ],
  );

  useEffect(() => {
    if (booking || !matchedShift) return;
    setValue("endTime", addMinutes(startTime, matchedShift.standardDuration), {
      shouldDirty: true,
    });
  }, [booking, matchedShift, setValue, startTime]);

  useEffect(() => {
    if (booking?.tableIds?.length === 0) {
      getFirstAvailableTable();
    }
  }, [getFirstAvailableTable, groupSize, endTime, startTime, booking]);

  useEffect(() => {
    reset({
      id: booking?.id ?? uuidv4(),
      createdAt: booking?.createdAt ?? new Date(),
      firstName: booking?.firstName ?? "",
      status: Status.active,
      lastName: booking?.lastName ?? "",
      startTime:
        booking?.startTime ?? nearestThirtyMinutes(selectedDate, "floor"),
      endTime:
        booking?.endTime ??
        addMinutes(
          nearestThirtyMinutes(selectedDate, "floor"),
          matchedShift?.standardDuration ?? DEFAULT_BOOKING_DURATION,
        ),
      tableIds: booking ? booking.tableIds : [],
      bookingStatus: booking?.bookingStatus ?? BookingStatus.active,
      groupSize: isBlock
        ? 0
        : (booking?.groupSize ?? DEFAULT_BOOKING_GROUP_SIZE),
      phone: booking?.phone ?? "",
      email: booking?.email ?? "",
      comment: booking?.comment ?? "",
      memo: booking?.memo ?? "",
      starred: booking?.starred ?? false,
      provider: Providers.allindex,
      labelIds: booking?.labels?.map((label) => label.id) ?? [],
    });
  }, [booking, selectedDate, matchedShift, reset, isBlock]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row justify-between gap-2 border-b h-12 relative">
        <Tabs>
          <TabPanel label={<Book />}>
            <Form {...form}>
              <form
                id="booking-form"
                className="flex flex-col gap-4 relative p-4"
                onSubmit={handleSubmit}
              >
                <div className="grid grid-cols-2 gap-4">
                  {booking?.comment && (
                    <div className="bg-warning p-2 col-span-full items-center flex flex-col gap-1 rounded text-center text-black">
                      <ChatIcon />
                      {booking.comment}
                    </div>
                  )}
                  {!isBlock && (
                    <div className="border-b col-span-full flex flex-col pb-4 gap-4">
                      {booking?.bookingStatus !== BookingStatus.pending &&
                        booking?.id && (
                          <>
                            <FormField
                              control={form.control}
                              name="bookingStatus"
                              render={({ field }) => (
                                <FormItem className="col-span-full">
                                  <FormLabel>Status</FormLabel>
                                  <FormControl>
                                    <BookingStatusSelect
                                      className="w-full"
                                      value={field.value}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onChange={(value) => {
                                        if (booking.id) {
                                          form.setValue(
                                            "bookingStatus",
                                            value,
                                            {
                                              shouldDirty: false,
                                            },
                                          );
                                          handleUpdateStatus(booking.id, value);
                                        } else {
                                          field.onChange(value);
                                        }
                                      }}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </>
                        )}
                      <FormField
                        control={form.control}
                        name="labelIds"
                        render={({ field }) => (
                          <FormItem>
                            <MultiSelect
                              maxSelect={1}
                              allowSelectAll={false}
                              placeholder="Select label..."
                              onValueChange={(value) => {
                                field.onChange(value);
                              }}
                              options={labels
                                .sort((a, b) => a.position - b.position)
                                .map((label) => ({
                                  label: label.text,
                                  value: label.id,
                                  icon: (props) => (
                                    <LabelIcon color={label.color} {...props} />
                                  ),
                                }))}
                              defaultValue={field.value}
                              value={field.value}
                            />
                            <FormMessage className="text-center" />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {!isBlock && (
                    <>
                      <FormField
                        control={form.control}
                        name="groupSize"
                        render={({ field }) => (
                          <FormItem className="col-span-full">
                            <FormLabel>{t("groupSize")}</FormLabel>
                            <FormControl>
                              <div className="flex flex-row gap-2">
                                <Button
                                  onClick={() => {
                                    field.onChange(1);
                                  }}
                                  type="button"
                                  variant={
                                    field.value === 1 ? "success" : "outline"
                                  }
                                >
                                  1
                                </Button>
                                <Button
                                  onClick={() => {
                                    field.onChange(2);
                                  }}
                                  type="button"
                                  variant={
                                    field.value === 2 ? "success" : "outline"
                                  }
                                >
                                  2
                                </Button>
                                <Button
                                  onClick={() => {
                                    field.onChange(3);
                                  }}
                                  type="button"
                                  variant={
                                    field.value === 3 ? "success" : "outline"
                                  }
                                >
                                  3
                                </Button>
                                <Button
                                  onClick={() => {
                                    field.onChange(4);
                                  }}
                                  type="button"
                                  variant={
                                    field.value === 4 ? "success" : "outline"
                                  }
                                >
                                  4
                                </Button>
                                <Button
                                  onClick={() => {
                                    field.onChange(5);
                                  }}
                                  type="button"
                                  variant={
                                    field.value === 5 ? "success" : "outline"
                                  }
                                >
                                  5
                                </Button>
                                <ModalInput
                                  min={1}
                                  className=""
                                  type="number"
                                  {...field}
                                >
                                  <Button
                                    variant={
                                      field.value > 5 ? "success" : "outline"
                                    }
                                    className="w-[100px]"
                                  >
                                    {field.value > 5 ? field.value : t("other")}
                                  </Button>
                                </ModalInput>
                              </div>
                            </FormControl>

                            <FormMessage className="text-center" />
                          </FormItem>
                        )}
                      />
                      <div className="border-b col-span-full"></div>
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("Customer.first_name")}</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder={t("Customer.first_name")}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("Customer.last_name")}</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder={t("Customer.last_name")}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem className="col-span-full">
                            <FormLabel>{t("Customer.phone")}</FormLabel>
                            <FormControl>
                              <PhoneInput defaultCountry="NL" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem className="col-span-full">
                            <FormLabel>{t("Customer.email")}</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                {...field}
                                placeholder={t("Customer.email")}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="col-span-full border-b"></div>
                    </>
                  )}

                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <FormItem className="col-span-full">
                        <VrodexPopover
                          className="p-0"
                          label={t("date")}
                          value={format(field.value, "d LLLL y")}
                          open={dateOpen}
                          onOpenChange={(open) => {
                            setDateOpen(open);
                            if (open) {
                              setStartTimeOpen(false);
                              setEndTimeOpen(false);
                            }
                          }}
                        >
                          {(setPopoverOpen) => (
                            <FormControl>
                              <Calendar
                                className="w-full"
                                classNames={{
                                  caption_end: "w-full",
                                  row: "justify-between w-full flex",
                                  head_row: "justify-between flex w-full",
                                }}
                                locale={getDateFNSLocale(locale)}
                                onDayClick={(date) => {
                                  field.onChange(
                                    set(date, {
                                      minutes: field.value.getMinutes(),
                                      hours: field.value.getHours(),
                                    }),
                                  );
                                  form.setValue(
                                    "endTime",
                                    set(date, {
                                      minutes: form
                                        .getValues("endTime")
                                        .getMinutes(),
                                      hours: form
                                        .getValues("endTime")
                                        .getHours(),
                                    }),
                                  );
                                  setPopoverOpen(false);
                                  setDateOpen(false);
                                  findMatchedShift(startTime);
                                }}
                                selected={field.value}
                              />
                            </FormControl>
                          )}
                        </VrodexPopover>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <FormItem className="col-span-1">
                        <VrodexPopover
                          open={startTimeOpen}
                          onOpenChange={(open) => {
                            setStartTimeOpen(open);
                            if (open) {
                              setEndTimeOpen(false);
                              setDateOpen(false);
                            }
                          }}
                          label={t("Booking.start_time")}
                          value={format(field.value, "HH:mm")}
                        >
                          {(setPopoverOpen) => (
                            <TimePicker
                              value={field.value}
                              onChange={(value) => {
                                console.log(value);
                                field.onChange(value);

                                setPopoverOpen(false);
                                findMatchedShift(value, true);
                              }}
                            />
                          )}
                        </VrodexPopover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="endTime"
                    render={({ field }) => (
                      <FormItem className="col-span-1">
                        <VrodexPopover
                          open={endTimeOpen}
                          onOpenChange={(open) => {
                            setEndTimeOpen(open);
                            if (open) {
                              setStartTimeOpen(false);
                            }
                          }}
                          label={t("Booking.end_time")}
                          value={format(field.value, "HH:mm")}
                        >
                          {(setPopoverOpen) => (
                            <TimePicker
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);

                                setPopoverOpen(false);
                                findMatchedShift(startTime);
                              }}
                            />
                          )}
                        </VrodexPopover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.formState.errors.activeShiftWarning && (
                    <span className="col-span-full text-orange-500 text-center text-sm">
                      {form.formState.errors.activeShiftWarning.message}
                    </span>
                  )}
                  {form.formState.errors.overlapWarning && (
                    <span className="col-span-full text-orange-500 text-center text-sm">
                      {form.formState.errors.overlapWarning.message}
                    </span>
                  )}
                  {form.formState.errors.endTimeBeforeStartError && (
                    <span className="col-span-full text-red-500 text-center text-sm">
                      {form.formState.errors.endTimeBeforeStartError.message}
                    </span>
                  )}

                  <div className="col-span-full border-b"></div>

                  <div className="col-span-full flex flex-col gap-2">
                    <Label>{t("Booking.section")}</Label>
                    <MultiSelect
                      maxSelect={1}
                      allowSelectAll={false}
                      placeholder="Select section..."
                      onValueChange={(value) => {
                        setSelectedSection(
                          sections.find((section) => section.id === value[0]),
                        );
                        form.setValue("tableIds", []);
                      }}
                      options={sections
                        .sort((a, b) => a.position - b.position)
                        .map((section) => ({
                          label: section.name,
                          value: section.id,
                        }))}
                      defaultValue={[selectedSection?.id]}
                      value={[selectedSection?.id]}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="tableIds"
                    render={({ field }) => (
                      <FormItem className="col-span-full">
                        <FormLabel>{t("Booking.table")}</FormLabel>
                        <MultiSelect
                          allowSelectAll={false}
                          placeholder="Select tables..."
                          onValueChange={(value) => {
                            field.onChange(value);
                          }}
                          options={tables
                            .filter(
                              (table) =>
                                table.locationId === selectedSection?.id,
                            )
                            .sort((a, b) => a.position - b.position)
                            .map((table) => ({
                              label: table.reference,
                              value: table.id,
                            }))}
                          defaultValue={field.value}
                          value={field.value}
                        />
                        <FormMessage className="text-center" />
                      </FormItem>
                    )}
                  />
                  {/* <Button
                    onClick={handleSelectBestTable}
                    variant="outline"
                    size="sm"
                    className="w-full col-span-full"
                  >
                    {t("Booking.select_best_table")}
                  </Button> */}
                  <div className="border-b col-span-full"></div>
                  <FormField
                    control={form.control}
                    name="memo"
                    render={({ field }) => (
                      <FormItem className="col-span-full">
                        <FormLabel>{t("Booking.memo")}</FormLabel>
                        <FormControl>
                          <Textarea className="h-full" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {!isBlock && (
                    <FormField
                      control={form.control}
                      name="starred"
                      render={({ field }) => (
                        <FormItem className="col-span-full space-y-0">
                          <label className="cursor-pointer flex items-center gap-3">
                            <FormControl>
                              <Checkbox
                                className="h-5 w-5"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <span>{t("Booking.starred")}</span>
                          </label>
                        </FormItem>
                      )}
                    />
                  )}

                  {booking?.id && (
                    <Button
                      onClick={handleDeleteBooking}
                      className="text-red-500 hover:text-red-500 col-span-full"
                      variant="ghost"
                    >
                      <DeleteForeverIcon />
                      {t("delete_booking")}
                    </Button>
                  )}
                </div>
                {form.formState.errors.root && (
                  <FormMessage>
                    {form.formState.errors.root.message}
                  </FormMessage>
                )}
              </form>
            </Form>
          </TabPanel>

          <TabPanel label={<SendIcon />}>
            <div className="flex flex-col gap-4 relative p-4 w-full flex-1">
              <Textarea
                value={messageToCustomer}
                onChange={(e) => setMessageToCustomer(e.target.value)}
                placeholder="Message to guest..."
              />
              <Button
                type="button"
                onClick={handleSendMessageToGuest}
                disabled={messageToCustomer.length === 0}
                variant="brand"
              >
                {t("Booking.message_to_customer_button_send")}
              </Button>
            </div>
          </TabPanel>

          <TabPanel label={<ClockIcon />}>
            <div className="p-4 flex flex-col gap-4">
              {booking?.audits
                ?.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
                .map((audit, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col rounded border bg-card"
                  >
                    <div className="p-2 border-b flex flex-row gap-1">
                      <span className="text-sm text-muted-foreground">
                        {format(
                          audit.createdAt,
                          user.clock === ClockTypes.military
                            ? "dd-MM-yyyy HH:mm:ss"
                            : "MM/dd/yyyy hh:aa:ss a",
                        )}
                      </span>
                      {audit.employeeId && (
                        <span className="text-sm text-muted-foreground">
                          {`- ${
                            employees.find(
                              (employee) => employee.id === audit.employeeId,
                            )?.firstName ?? "Unknown"
                          }`}
                        </span>
                      )}
                      {audit.source !== BookingAuditSource.pos && (
                        <span className="text-sm text-muted-foreground">{`| ${audit.source}`}</span>
                      )}
                    </div>
                    <div className="p-2 text-sm">
                      {audit.type === BookingAuditTypes.created && (
                        <span>{t("Booking.history_created_at_label")}</span>
                      )}
                      {audit.type === BookingAuditTypes.status && (
                        <span>
                          {t("Booking.history_status_label", {
                            old_value: audit.oldValue,
                            new_value: audit.newValue,
                          })}
                        </span>
                      )}
                      {audit.type === BookingAuditTypes.group_size && (
                        <span>
                          {t("Booking.history_group_size_label", {
                            old_value: audit.oldValue,
                            new_value: audit.newValue,
                          })}
                        </span>
                      )}
                      {audit.type === BookingAuditTypes.start_time &&
                        audit.oldValue && (
                          <span>
                            {t("Booking.history_start_time_label", {
                              old_value: format(
                                audit.oldValue,
                                user.clock === ClockTypes.military
                                  ? "dd-MM-yyyy HH:mm"
                                  : "MM/dd/yyyy hh:aa a",
                              ),
                              new_value: format(
                                audit.newValue,
                                user.clock === ClockTypes.military
                                  ? "dd-MM-yyyy HH:mm:ss"
                                  : "MM/dd/yyyy hh:aa:ss a",
                              ),
                            })}
                          </span>
                        )}
                      {audit.type === BookingAuditTypes.end_time &&
                        audit.oldValue && (
                          <span>
                            {t("Booking.history_end_time_label", {
                              old_value: format(
                                audit.oldValue,
                                user.clock === ClockTypes.military
                                  ? "dd-MM-yyyy HH:mm"
                                  : "MM/dd/yyyy hh:aa a",
                              ),
                              new_value: format(
                                audit.newValue,
                                user.clock === ClockTypes.military
                                  ? "dd-MM-yyyy HH:mm:ss"
                                  : "MM/dd/yyyy hh:aa:ss a",
                              ),
                            })}
                          </span>
                        )}
                      {audit.type === BookingAuditTypes.first_name && (
                        <span>
                          {t("Booking.history_first_name_label", {
                            old_value: audit.oldValue ?? "Walk in",
                            new_value: audit.newValue,
                          })}
                        </span>
                      )}
                      {audit.type === BookingAuditTypes.last_name &&
                        audit.oldValue && (
                          <span>
                            {t("Booking.history_last_name_changed_label", {
                              old_value: audit.oldValue,
                              new_value: audit.newValue,
                            })}
                          </span>
                        )}
                      {audit.type === BookingAuditTypes.last_name &&
                        !audit.oldValue && (
                          <span>
                            {t("Booking.history_last_name_added_label", {
                              new_value: audit.newValue,
                            })}
                          </span>
                        )}

                      {audit.type === BookingAuditTypes.phone_number &&
                        audit.oldValue && (
                          <span>
                            {t("Booking.history_phone_changed_label", {
                              old_value: audit.oldValue,
                              new_value: audit.newValue,
                            })}
                          </span>
                        )}
                      {audit.type === BookingAuditTypes.phone_number &&
                        !audit.oldValue && (
                          <span>
                            {t("Booking.history_phone_added_label", {
                              new_value: audit.newValue,
                            })}
                          </span>
                        )}
                      {audit.type === BookingAuditTypes.internal_memo &&
                        audit.oldValue && (
                          <span>
                            {t("Booking.history_memo_changed_label", {
                              old_value: audit.oldValue,
                              new_value: audit.newValue,
                            })}
                          </span>
                        )}
                      {audit.type === BookingAuditTypes.internal_memo &&
                        !audit.oldValue && (
                          <span>
                            {t("Booking.history_memo_added_label", {
                              new_value: audit.newValue,
                            })}
                          </span>
                        )}
                      {audit.type === BookingAuditTypes.table &&
                        audit.oldValue && (
                          <span>
                            {t("Booking.history_table_changed_label", {
                              old_value: audit.oldValue
                                .split(",")
                                .map(
                                  (id) =>
                                    tables.find((table) => table.id === id)
                                      ?.reference,
                                )
                                .filter(Boolean)
                                .join(","),
                              new_value: audit.newValue
                                .split(",")
                                .map(
                                  (id) =>
                                    tables.find((table) => table.id === id)
                                      ?.reference,
                                )
                                .filter(Boolean)
                                .join(","),
                            })}
                          </span>
                        )}
                      {audit.type === BookingAuditTypes.table &&
                        !audit.oldValue && (
                          <span>
                            {t("Booking.history_table_added_label", {
                              new_value: audit.newValue
                                .split(",")
                                .map(
                                  (id) =>
                                    tables.find((table) => table.id === id)
                                      ?.reference,
                                )
                                .filter(Boolean)
                                .join(","),
                            })}
                          </span>
                        )}
                      {audit.type === BookingAuditTypes.email &&
                        isBookingAuditMessage(audit.newValue) && (
                          <>
                            {audit.newValue ===
                              BookingAuditMessage.booking_confirmed && (
                              <span>
                                {t("Booking.history_confirmed_email_label")}
                              </span>
                            )}
                            {audit.newValue ===
                              BookingAuditMessage.booking_canceled && (
                              <span>
                                {t("Booking.history_canceled_email_label")}
                              </span>
                            )}
                          </>
                        )}
                      {audit.type === BookingAuditTypes.email &&
                        !isBookingAuditMessage(audit.newValue) && (
                          <>
                            <span>
                              {t("Booking.history_custom_email_label", {
                                message: audit.newValue,
                              })}
                            </span>
                          </>
                        )}
                    </div>
                  </div>
                ))}
            </div>
          </TabPanel>
        </Tabs>
      </div>
    </div>
  );
}
